'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './blog.module.css';

/* ── Article content ── */
const articles: Record<string, { tag: string; tagClass: string; title: string; meta: string; body: string }> = {
  agentic: {
    tag: 'Featured', tagClass: 'featured',
    title: "Agentic AI Is Here: How Autonomous Systems Are Redefining What's Possible in Enterprise",
    meta: 'Novamind AI Editorial · March 2026 · 8 min read',
    body: `<p>For years, AI meant answering questions. You asked, it responded. The interaction was transactional, contained, and fundamentally passive. That era is ending.</p>
<h3>What Is Agentic AI?</h3>
<p>Agentic AI refers to systems that don't just respond — they <strong>act</strong>. These systems can break down complex goals into sub-tasks, execute them across multiple tools and systems, handle failures, and loop back to refine their approach — all without a human in the loop.</p>
<p>Think of the difference between a calculator and an accountant. One answers your question. The other manages your books, flags anomalies, files your returns, and tells you what to do next quarter.</p>
<h3>Why It Matters for Enterprise</h3>
<p>The enterprise implications are significant. Customer service agents that don't just answer questions but resolve issues end-to-end. Sales assistants that qualify leads, schedule demos, and follow up — autonomously. Operations systems that detect inefficiencies and fix them without escalation.</p>
<p>At Novamind AI, our voice agents already operate agentically within defined domains — handling calls, routing inquiries, updating records, and escalating only when necessary. This is agentic AI applied practically, today.</p>
<h3>The Road Ahead</h3>
<p>The businesses that invest in agentic AI now are building operational leverage that compounds over time. Every task an agent handles is a task your team doesn't. Every interaction it learns from makes the next one better.</p>
<p>The question isn't whether agentic AI will transform your industry. It's whether you'll lead that transformation or respond to it.</p>`
  },
  healthcare: {
    tag: 'Healthcare', tagClass: 'health',
    title: "Healthcare Voice Agents Are Cutting Admin Time by 40% — Here's How",
    meta: 'Novamind AI Editorial · March 2026 · 6 min read',
    body: `<p>Administrative burden is one of the biggest challenges facing healthcare providers today. Clinicians spend nearly two hours on paperwork for every hour of direct patient care.</p>
<h3>What Voice Agents Handle</h3>
<p>AI voice agents deployed at clinics and hospitals are now managing the full front-desk workflow: <strong>patient intake, appointment scheduling, insurance verification, prescription refill requests, and FAQ handling</strong> — 24 hours a day, 7 days a week.</p>
<p>The result? Staff who were spending 60% of their day on phone calls now spend that time on patient care. Response times drop from minutes to seconds.</p>
<h3>The 40% Number</h3>
<p>Across deployments in primary care, dental, and specialist clinics, practices report an average 40% reduction in administrative time within the first 90 days. That's not a projection — that's measured, operational reality.</p>`
  },
  restaurant: {
    tag: 'Restaurants', tagClass: 'industry',
    title: "The Restaurant Industry's AI Revolution: From Orders to Full Operations",
    meta: 'Novamind AI Editorial · February 2026 · 5 min read',
    body: `<p>The restaurant industry runs on thin margins and thinner staff. Every missed call is a missed reservation. Every unanswered question is a customer who goes somewhere else.</p>
<h3>Beyond the Phone</h3>
<p>Today's restaurant voice agents handle <strong>reservations, takeout orders, menu questions, hours, dietary inquiries, and loyalty program management</strong> — simultaneously, without hold times, in multiple languages.</p>
<h3>What the Numbers Show</h3>
<p>Restaurants using Novamind AI's voice agents report a 35% increase in reservations captured outside business hours. Order accuracy improves because the AI never mishears and always confirms.</p>`
  },
  'voice-accuracy': {
    tag: 'Voice AI', tagClass: 'voice',
    title: 'Voice AI Accuracy Surpasses Human Benchmarks in Real-World Tests',
    meta: 'Novamind AI Editorial · February 2026 · 4 min read',
    body: `<p>For a long time, voice AI's biggest weakness was accuracy. Accents, background noise, complex queries — these were reliable failure points. That has fundamentally changed.</p>
<h3>The Benchmark Results</h3>
<p>In controlled real-world testing, AI voice systems now demonstrate <strong>higher accuracy rates than human agents</strong> on structured tasks — appointment booking, order taking, FAQ resolution — across a range of accents, noise levels, and query complexity.</p>
<p>Human agents average a 91% first-call resolution rate on routine tasks. Leading AI voice systems now average 94–96%, with zero fatigue degradation across shifts.</p>
<h3>Why This Changes Everything</h3>
<p>Accuracy was the last credible objection to AI voice deployment at scale. The question is no longer whether AI can handle your calls as well as a human. It's how fast you can get it deployed.</p>`
  },
  regulation: {
    tag: 'Industry News', tagClass: 'news',
    title: 'AI Regulation in 2026: What Every Business Leader Needs to Know Now',
    meta: 'Novamind AI Editorial · January 2026 · 7 min read',
    body: `<p>AI regulation is no longer a future concern — it's a present operational reality. The EU AI Act is in force. US state-level AI bills are multiplying.</p>
<h3>What's Actually Required</h3>
<p>For most businesses deploying AI in customer-facing roles, the key requirements center on <strong>transparency, data handling, and auditability</strong>. Customers must be informed they're interacting with AI.</p>
<h3>What This Means for Voice Deployments</h3>
<p>Voice AI deployments face requirements around call recording consent, data retention limits, and mandatory human escalation pathways. Novamind AI builds compliance-ready infrastructure into every deployment.</p>`
  },
  ivr: {
    tag: 'Enterprise', tagClass: 'enterprise',
    title: "From IVR to Intelligence: Why Voice AI Is Replacing Phone Trees Forever",
    meta: 'Novamind AI Editorial · January 2026 · 5 min read',
    body: `<p>Press 1 for billing. Press 2 for support. IVR systems have been universally disliked for three decades. They persist because, until recently, there was no better alternative at scale.</p>
<h3>Why IVR Fails</h3>
<p>IVR systems are rigid, impersonal, and frustrating. They force customers into pre-defined paths that rarely match their actual needs.</p>
<h3>What AI Voice Agents Do Differently</h3>
<p>AI voice agents understand <strong>natural language</strong>. A customer who says "I need to move my appointment and also check if you accept my new insurance" gets a single, fluid conversation that handles both. Resolution rates are higher. Handle times are shorter. Customer satisfaction scores improve.</p>`
  },
  cost: {
    tag: 'Voice AI', tagClass: 'voice',
    title: "The True Cost of Waiting: Why Businesses That Delay AI Fall Behind",
    meta: 'Novamind AI Editorial · December 2025 · 6 min read',
    body: `<p>Every business leader knows AI is coming. Most are waiting — for the technology to mature, for a clearer ROI case, for someone else to go first. That wait has a cost.</p>
<h3>The Compounding Disadvantage</h3>
<p>AI advantage compounds. Businesses that deploy early accumulate data, refine models, and build operational muscle. Their AI gets better every month. Businesses that wait start from zero.</p>
<h3>The Operational Cost</h3>
<p>The pure operational cost of not having AI voice deployed is measurable: <strong>missed calls outside business hours, staff time spent on routine inquiries, higher error rates, and customer churn</strong> from poor response times.</p>`
  },
  multimodal: {
    tag: 'Enterprise', tagClass: 'enterprise',
    title: 'Multimodal AI: The Next Frontier for Enterprise Deployments',
    meta: 'Novamind AI Editorial · March 2026 · 5 min read',
    body: `<p>Until recently, enterprise AI was single-modal: text in, text out. The next generation processes <strong>voice, text, images, and documents simultaneously</strong>.</p>
<h3>What Multimodal Means in Practice</h3>
<p>A multimodal AI agent in a healthcare setting can listen to a patient describe symptoms, reference uploaded lab results, cross-check against clinical guidelines, and respond verbally — all in one interaction.</p>`
  },
  smb: {
    tag: 'Enterprise', tagClass: 'enterprise',
    title: 'Small Business, Big AI: How SMBs Are Competing with Enterprise Giants',
    meta: 'Novamind AI Editorial · February 2026 · 4 min read',
    body: `<p>For years, the most powerful AI tools were only accessible to companies with hundred-million-dollar technology budgets. That asymmetry is collapsing.</p>
<h3>The Democratization of AI</h3>
<p>Cloud infrastructure and platforms like Novamind AI have made enterprise-grade AI voice technology available to any business. A two-location restaurant group can now deploy the same caliber of customer service AI as a national chain.</p>
<h3>The Competitive Reality</h3>
<p>SMBs that deploy AI now don't just keep up — they <strong>pull ahead of larger competitors</strong> who move slowly. The playing field isn't just leveling. For agile small businesses, it's tilting in their favor.</p>`
  },
  availability: {
    tag: 'Voice AI', tagClass: 'voice',
    title: "Why 24/7 AI Availability Is No Longer a Luxury — It's a Standard",
    meta: 'Novamind AI Editorial · January 2026 · 3 min read',
    body: `<p>Customer expectations have shifted permanently. Businesses that shaped modern consumer behavior have trained customers to expect immediate responses at any hour.</p>
<h3>The After-Hours Problem</h3>
<p>For most businesses, 30–40% of inbound inquiries arrive outside business hours. Without AI, those inquiries go to voicemail — and over 60% of callers who reach voicemail don't leave a message. They call a competitor.</p>
<h3>24/7 as Competitive Baseline</h3>
<p>With Novamind AI, 24/7 availability is included in every deployment. Not an add-on. Not an upgrade. The standard.</p>`
  },
  anatomy: {
    tag: 'Voice AI', tagClass: 'voice',
    title: 'The Anatomy of a Successful AI Voice Agent Deployment',
    meta: 'Novamind AI Editorial · December 2025 · 6 min read',
    body: `<p>Not all AI voice deployments succeed. The technology is only part of the equation.</p>
<h3>Step 1: Define the Scope Precisely</h3>
<p>The most successful deployments start narrow. Pick the single highest-volume, most repetitive call type your business handles. Master that one flow before expanding.</p>
<h3>Step 2: Feed It Your Knowledge</h3>
<p>Upload your menu, FAQs, hours, and policies. The more context the agent has, the more accurately it handles edge cases.</p>
<h3>Step 3: Design the Escalation Path</h3>
<p>Define clearly when the AI should hand off to a human — and make that handoff seamless.</p>
<h3>Step 4: Measure and Iterate</h3>
<p>Track resolution rate, handle time, escalation rate, and customer satisfaction. Deployments that iterate consistently outperform static ones by a factor of three within six months.</p>`
  },
};

const FILTERS = ['All', 'Voice AI', 'Healthcare', 'Restaurants', 'Enterprise', 'Industry News'];

const externalLinks = [
  { num: '01', title: 'Anthropic Research & News', domain: 'anthropic.com', url: 'https://www.anthropic.com/news' },
  { num: '02', title: "The Batch — Andrew Ng's Weekly AI Digest", domain: 'deeplearning.ai', url: 'https://www.deeplearning.ai/the-batch/' },
  { num: '03', title: 'Google DeepMind Blog', domain: 'deepmind.google', url: 'https://deepmind.google/discover/blog/' },
  { num: '04', title: 'MIT Technology Review — AI', domain: 'technologyreview.com', url: 'https://www.technologyreview.com/topic/artificial-intelligence/' },
];

export default function BlogPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [modalArticle, setModalArticle] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  const openArticle = (id: string) => setModalArticle(id);
  const closeModal = () => setModalArticle(null);

  const filterKey = activeFilter.toLowerCase().replace(/\s+/g, '-');

  const isVisible = (category: string) =>
    activeFilter === 'All' || category === filterKey;

  const article = modalArticle ? articles[modalArticle] : null;

  return (
    <div className={styles.page}>
      <NavBar />

      <div className={styles.blogPage}>

        {/* ── Header ── */}
        <div className={styles.blogHeader}>
          <div className={styles.blogHeaderLeft}>
            <h1>AI Insights</h1>
            <p>News, trends, and perspectives from the frontier of enterprise AI.</p>
          </div>
          <div className={styles.blogHeaderRight}>Updated Weekly</div>
        </div>

        {/* ── Filters ── */}
        <div className={styles.blogFilters}>
          {FILTERS.map(f => (
            <button
              key={f}
              className={`${styles.blogFilter} ${activeFilter === f ? styles.active : ''}`}
              onClick={() => setActiveFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>

        {/* ── Featured ── */}
        {isVisible('all') && (
          <div className={styles.blogFeatured} onClick={() => openArticle('agentic')}>
            <div className={styles.blogFeaturedBg}>
              <Image src="/images/blog.png" alt="Featured" fill style={{ objectFit: 'cover', objectPosition: 'center' }} />
            </div>
            <div className={styles.blogFeaturedOverlay} />
            <div className={styles.blogFeaturedContent}>
              <span className={`${styles.blogTag} ${styles.featured}`}>Featured</span>
              <h2>Agentic AI Is Here: How Autonomous Systems Are Redefining What&apos;s Possible in Enterprise</h2>
              <p>The next wave of AI isn&apos;t just answering questions — it&apos;s taking action. Agentic AI systems now handle multi-step tasks, make decisions, and operate continuously without human intervention.</p>
              <div className={styles.blogMeta}>
                <span>Novamind AI Editorial</span>
                <span>&middot;</span>
                <span>March 2026</span>
                <span>&middot;</span>
                <span>8 min read</span>
                <span className={styles.blogReadMore}>Read Article &rarr;</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Grid Row 1 ── */}
        <div className={styles.blogGridRow1}>
          {isVisible('healthcare') && (
            <div className={styles.blogCard} onClick={() => openArticle('healthcare')}>
              <div className={`${styles.blogCardImg} ${styles.blogCardImgLarge}`}>
                <Image src="/images/nurse.png" alt="Healthcare" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className={styles.blogCardBody}>
                <span className={`${styles.blogTag} ${styles.health}`}>Healthcare</span>
                <h3>Healthcare Voice Agents Are Cutting Admin Time by 40% — Here&apos;s How</h3>
                <p>Clinics and hospitals deploying AI voice agents are reporting dramatic reductions in front-desk workload. Patient intake, appointment scheduling, and FAQ handling are now fully automated.</p>
                <div className={styles.blogCardMeta}><span>March 2026</span><span className={styles.dot} /><span>6 min read</span></div>
              </div>
            </div>
          )}
          {isVisible('restaurants') && (
            <div className={styles.blogCard} onClick={() => openArticle('restaurant')}>
              <div className={styles.blogCardImg}>
                <Image src="/images/restaurant.jpg" alt="Restaurant" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className={styles.blogCardBody}>
                <span className={`${styles.blogTag} ${styles.industry}`}>Restaurants</span>
                <h3>The Restaurant Industry&apos;s AI Revolution: From Orders to Full Operations</h3>
                <p>Voice AI is no longer just for tech giants. Independent restaurants are now automating reservations, takeout orders, and customer FAQs — at a fraction of the cost.</p>
                <div className={styles.blogCardMeta}><span>February 2026</span><span className={styles.dot} /><span>5 min read</span></div>
              </div>
            </div>
          )}
          {isVisible('voice-ai') && (
            <div className={styles.blogCard} onClick={() => openArticle('voice-accuracy')}>
              <div className={styles.blogCardImg}>
                <Image src="/images/call.jpg" alt="Voice AI" fill style={{ objectFit: 'cover' }} />
              </div>
              <div className={styles.blogCardBody}>
                <span className={`${styles.blogTag} ${styles.voice}`}>Voice AI</span>
                <h3>Voice AI Accuracy Surpasses Human Benchmarks in Real-World Tests</h3>
                <p>New benchmarks show AI voice systems outperforming human agents in accuracy, response speed, and consistency across noisy environments and complex queries alike.</p>
                <div className={styles.blogCardMeta}><span>February 2026</span><span className={styles.dot} /><span>4 min read</span></div>
              </div>
            </div>
          )}
        </div>

        {/* ── Grid Row 2 ── */}
        <div className={styles.blogGridRow2}>
          {isVisible('industry-news') && (
            <div className={styles.blogCard} onClick={() => openArticle('regulation')}>
              <div className={styles.blogCardBodyText}>
                <span className={`${styles.blogTag} ${styles.news}`}>Industry News</span>
                <h3>AI Regulation in 2026: What Every Business Leader Needs to Know Now</h3>
                <p>Governments across the US and EU are moving fast on AI compliance frameworks. Here&apos;s a plain-language breakdown of what&apos;s changing and how to stay ahead.</p>
                <div className={styles.blogCardMeta}><span>January 2026</span><span className={styles.dot} /><span>7 min read</span></div>
              </div>
            </div>
          )}
          {isVisible('enterprise') && (
            <div className={styles.blogCard} onClick={() => openArticle('ivr')}>
              <div className={styles.blogCardBodyText}>
                <span className={`${styles.blogTag} ${styles.enterprise}`}>Enterprise</span>
                <h3>From IVR to Intelligence: Why Voice AI Is Replacing Phone Trees Forever</h3>
                <p>Customers have tolerated clunky IVR systems for decades. AI voice agents finally offer a replacement that customers actually prefer.</p>
                <div className={styles.blogCardMeta}><span>January 2026</span><span className={styles.dot} /><span>5 min read</span></div>
              </div>
            </div>
          )}
          {isVisible('voice-ai') && (
            <div className={styles.blogCard} onClick={() => openArticle('cost')}>
              <div className={styles.blogCardBodyText}>
                <span className={`${styles.blogTag} ${styles.voice}`}>Voice AI</span>
                <h3>The True Cost of Waiting: Why Businesses That Delay AI Fall Behind</h3>
                <p>Every month without AI is a month your competitors are pulling ahead. We break down the real operational cost of delayed AI adoption — with numbers.</p>
                <div className={styles.blogCardMeta}><span>December 2025</span><span className={styles.dot} /><span>6 min read</span></div>
              </div>
            </div>
          )}
        </div>

        {/* ── Trending ── */}
        <div className={styles.blogTrending}>
          <div className={styles.blogTrendingLabel}>Trending Now</div>
          <div className={styles.blogTrendingList}>
            {[
              { id: 'multimodal', num: '01', title: 'Multimodal AI: The Next Frontier for Enterprise Deployments', read: '5 min read' },
              { id: 'smb',        num: '02', title: 'Small Business, Big AI: How SMBs Are Competing with Enterprise Giants', read: '4 min read' },
              { id: 'availability', num: '03', title: "Why 24/7 AI Availability Is No Longer a Luxury — It's a Standard", read: '3 min read' },
              { id: 'anatomy',    num: '04', title: 'The Anatomy of a Successful AI Voice Agent Deployment', read: '6 min read' },
            ].map(item => (
              <div key={item.id} className={styles.blogTrendingItem} onClick={() => openArticle(item.id)}>
                <span className={styles.blogTrendingNum}>{item.num}</span>
                <div className={styles.blogTrendingText}>
                  <h4>{item.title}</h4>
                  <span>{item.read}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Further Reading ── */}
        <div className={`${styles.blogTrending} ${styles.blogFurtherReading}`}>
          <div className={styles.blogTrendingLabel}>Further Reading — Trusted External Sources</div>
          <div className={styles.blogTrendingList}>
            {externalLinks.map(link => (
              <div key={link.num} className={styles.blogTrendingItem} onClick={() => window.open(link.url, '_blank', 'noopener')}>
                <span className={styles.blogTrendingNum}>{link.num}</span>
                <div className={styles.blogTrendingText}>
                  <h4>{link.title}</h4>
                  <span>{link.domain}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Newsletter ── */}
        <div className={styles.blogNewsletter}>
          <div className={styles.blogNewsletterText}>
            <h2>Stay ahead of the AI curve.</h2>
            <p>Weekly insights on enterprise AI, voice technology, and industry trends — delivered to your inbox.</p>
          </div>
          <div className={styles.blogNewsletterForm}>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <Link href="/contact" className={styles.btnPrimary}>Subscribe</Link>
          </div>
        </div>

      </div>

      {/* ── Article Modal ── */}
      {article && (
        <div className={styles.articleModal} onClick={closeModal}>
          <div className={styles.articleModalPanel} onClick={e => e.stopPropagation()}>
            <div className={styles.articleModalClose}>
              <button onClick={closeModal}>&times;</button>
            </div>
            <div className={styles.articleModalContent}>
              <span className={`${styles.blogTag} ${styles[article.tagClass]}`}>{article.tag}</span>
              <h2 className={styles.articleModalTitle}>{article.title}</h2>
              <div className={styles.articleModalMeta}>{article.meta}</div>
              <div
                className={styles.articleModalBody}
                dangerouslySetInnerHTML={{ __html: article.body }}
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
